import { FunctionComponent, useEffect, useRef, useState } from 'react';
import './App.css';
import { NavBar } from './components/Compounds/Navigation/NavBar';
import { SideNavBar } from './components/Compounds/Navigation/SideNavBar';
import AppRoutes from './components/AppRoutes';
import { PortalSupportMenu } from './components/Compounds/PortalSupportMenu/';
import { LoadingIconPage } from './loadingIcon';
import { getUserLoadingSubject, getUserSubject, isInternalUser, useSharedUser } from './store/User';
import { defaultFeatureFlags, FeatureFlags, getFeatureFlagSubject, useFeatureFlags } from './Api/useFeatureFlags';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useSession } from './Api/useSession';
import { ImpersonationBanner } from './components/Compounds/UserCreation/ImpersonationBanner';
import { User } from './types';
import PublicAppRoutes from './components/PublicAppRoutes';
import { DocsView, TosModal } from './components/Pages';
import { useErrorStore } from './store/Error';
import { useGeneralStore } from './store/GeneralStore';
import { LicenseInfo } from '@mui/x-license';
import { useSharedConfiguration } from './store/Configuration';
import { useSuppliersStore } from './store/Supplier';
import { safePathName } from './utils/helpers';
import { isTokenExpired } from './Api/callExternalApi';
import { isStandalonePage, isUnprotectedRoute } from './utils/unprotectedRoutes';

const App: FunctionComponent = () => {
  LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_LICENSE || '');
  // Init general store, user, and feature flags.
  useFeatureFlags();
  useGeneralStore();
  useSharedUser();

  const { errorMessageSubscription } = useErrorStore();
  const { tokenLoading, clearSession } = useSession();
  const [userLoading, setUserLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  // The error subscription below is registered once, so it has to read the route through a ref instead of its closure.
  const locationRef = useRef(location);
  const { fetchSuppliers } = useSuppliersStore();
  const { fetchConfiguration } = useSharedConfiguration();
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>(defaultFeatureFlags);
  const [params] = useSearchParams();
  const [user, setUser] = useState<User | null>(null);
  const showFetching: boolean = [tokenLoading, userLoading].some((loadingState) => loadingState);

  if (featureFlags.maintenance_mode && location.pathname != '/portal/offline') navigate('/portal/offline');
  if (!featureFlags.maintenance_mode && location.pathname == '/portal/offline') window.location.href = '/';

  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  useEffect(() => {
    const flags = getFeatureFlagSubject().subscribe((flags) => setFeatureFlags(flags));
    const userSubscription = getUserSubject().subscribe((user) => setUser(user));
    const userLoadingSub = getUserLoadingSubject().subscribe((loading) => setUserLoading(loading));
    const errorSubscription = errorMessageSubscription.subscribe((errors) => {
      if (!errors) return;

      const { pathname } = locationRef.current;
      const eLower = errors.toLowerCase();
      // A 401 only means the session is over when the token itself is spent. An account the user has lost
      // access to answers 401 too, and logging them out for that strands them: the account is still their
      // saved scope, so signing back in reselects it and returns the same 401.
      const sessionOver = errors.includes('401') && isTokenExpired(localStorage.getItem('token'));
      if (
        (sessionOver ||
          eLower.includes('token') ||
          eLower.includes('authenticated') ||
          eLower.includes('not authorized')) &&
        !isUnprotectedRoute(pathname)
      ) {
        clearSession('Session expired. Please login again', true);
        return;
      }

      if (errors?.includes('403') || (errors?.includes('404') && pathname.includes('/login'))) return;
    });

    return () => {
      if (flags) flags?.unsubscribe();
      if (userSubscription) userSubscription.unsubscribe();
      if (errorSubscription) errorSubscription.unsubscribe();
      if (userLoadingSub) userLoadingSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const path = location.pathname;

    if (!localStorage.getItem('token') && !isUnprotectedRoute(path)) {
      clearSession();
    } else if (localStorage.getItem('token')) {
      const redirectTo: string | null = params.get('redirectTo');
      if (redirectTo) navigate(safePathName(redirectTo));
    }
  }, []);

  useEffect(() => {
    if (user?.id) {
      fetchSuppliers();
      fetchConfiguration();
    }
  }, [user?.id]);

  const isDocsPage = location.pathname.startsWith('/docs');
  const publicPage = featureFlags.maintenance_mode || isStandalonePage(location.pathname);

  // The docs viewer renders full-screen with no main-app chrome (no NavBar or
  // SideNavBar). It is gated behind the `docs_viewer` feature flag, then still
  // requires an authenticated user; the unprotected-route guard above redirects
  // to /login when no token is present. Any signed-in user may read it, since
  // the API trims it to their roles.
  if (featureFlags.docs_viewer && !showFetching && !publicPage && user?.id && user.tos_read && isDocsPage) {
    return <DocsView canSimulateRole={isInternalUser(user)} />;
  }

  if (user?.id && !user.tos_read && !publicPage) {
    return (
      <div className="h-screen flex">
        <div
          data-testid="private-app"
          className="flex flex-col basis-full main-container">
          <ImpersonationBanner />
          <TosModal />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {user && user.id && !publicPage && <SideNavBar />}
      {showFetching && !publicPage && <LoadingIconPage />}
      {!showFetching && !publicPage && user && user.id ? (
        <div
          data-testid="private-app"
          className="flex flex-col basis-full main-container">
          <ImpersonationBanner />
          <NavBar />
          <AppRoutes />
          <PortalSupportMenu showDocs={!!featureFlags.docs_viewer} />
        </div>
      ) : (
        <div
          data-testid="public-app"
          className="flex flex-col basis-full main-container">
          <PublicAppRoutes />
        </div>
      )}
    </div>
  );
};

export default App;
